/**
 * ProtectedRoute - Schützt Routen vor unauthentifizierten Zugriffen
 */

import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { ShieldAlert } from 'lucide-react'

import { useAuthStore } from '@/store/authStore'
import { useModuleStore } from '@/store/moduleStore'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'

// =============================================================================
// PROPS
// =============================================================================

interface ProtectedRouteProps {
  children: ReactNode
  /** Erforderliche Permission (optional) */
  permission?: string
  /** Erforderliche Rolle (optional) */
  role?: string
  /** Eine der Rollen muss vorhanden sein (OR-Logik, optional) */
  roles?: string[]
  /** Rollenpruefung ohne Admin-Bypass */
  strictRoles?: string[]
  /** Wenn gesetzt, wird die Route nur angezeigt wenn dieses Modul aktiviert ist */
  requiredModule?: string
}

// =============================================================================
// ACCESS DENIED COMPONENT
// =============================================================================

function AccessDenied() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <ShieldAlert className="h-8 w-8 text-destructive" />
            </div>
          </div>
          <CardTitle className="text-xl">Zugriff verweigert</CardTitle>
          <CardDescription>
            Sie haben keine Berechtigung, auf diese Seite zuzugreifen.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <Button variant="outline" onClick={() => window.history.back()}>
            Zurück
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

// =============================================================================
// PROTECTED ROUTE COMPONENT
// =============================================================================

export function ProtectedRoute({
  children,
  permission,
  role,
  roles,
  strictRoles,
  requiredModule,
}: ProtectedRouteProps) {
  const location = useLocation()
  const { isAuthenticated, hasPermission, hasRole, isAdmin } = useAuthStore()
  const { isModuleEnabled } = useModuleStore()

  // Nicht authentifiziert -> Login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Modul-Check: deaktiviertes Modul -> Dashboard
  if (requiredModule && !isModuleEnabled(requiredModule)) {
    return <Navigate to="/dashboard" replace />
  }

  // Admin hat immer Zugriff
  if (!isAdmin()) {
    // Permission-Check
    if (permission && !hasPermission(permission)) {
      return <AccessDenied />
    }

    // Role-Check (einzelne Rolle)
    if (role && !hasRole(role)) {
      return <AccessDenied />
    }

    // Roles-Check (eine der Rollen muss vorhanden sein)
    if (roles && roles.length > 0 && !roles.some((r) => hasRole(r))) {
      return <AccessDenied />
    }
  }

  if (strictRoles && strictRoles.length > 0 && !strictRoles.some((r) => hasRole(r))) {
    return <AccessDenied />
  }

  return <>{children}</>
}

export default ProtectedRoute
